/* eslint-disable max-len */
/**
 * Dependencies:
 * - Custom select component
 * - Fetch cache (theme.fetchCache)
 * - Quickbuy content transformer (theme.quickBuy.convertToQuickBuyContent)
 *
 * Required translation strings:
 * - noStock
 * - noVariant
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.quickBuyContainer = this.closest('.js-quickbuy');
      this.featuredProductContainer = this.closest('.cc-featured-product');
      this.section = this.quickBuyContainer
                     || this.featuredProductContainer
                     || this.closest('.shopify-section');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.productAvailable = this.classList.contains('variant-picker--product-available');
      this.variant = this.getVariantData();
      this.variantRequestId = 0;

      this.addListeners();
      this.updateVariantContent();
      if (!this.quickBuyContainer && !this.featuredProductContainer) {
        setTimeout(this.applySearchParams.bind(this), 0);
      }
      this.setAttribute('loaded', '');
    }

    addListeners() {
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.addEventListener('change', this.boundHandleVariantChange);

      this.boundHandleLabelMouseEnter = this.handleLabelMouseEnter.bind(this);
      this.querySelectorAll('.opt-label, .custom-select__option').forEach((el) => {
        el.addEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.addEventListener('touchstart', this.boundHandleLabelMouseEnter, { passive: true, once: true });
        el.addEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
      });
    }

    removeListeners() {
      this.removeEventListener('change', this.boundHandleVariantChange);
      this.querySelectorAll('.opt-label, .custom-select__option').forEach((el) => {
        el.removeEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.removeEventListener('touchstart', this.boundHandleLabelMouseEnter);
        el.removeEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
      });
    }

    /**
     * Update parts of this component that require JS to display correctly.
     */
    updateVariantContent() {
      this.updateStatusClasses();
      this.updateAddToCartButton();
      this.updateSwatchLabel();
      this.updateAvailability();
    }

    getProductForm() {
      return this.section.querySelector('.js-product-form');
    }

    /**
     * Handles 'mouseenter' events on option labels. Preloads links.
     * @param {object} evt - Event object.
     */
    handleLabelMouseEnter(evt) {
      const label = evt.currentTarget;
      if (label.dataset.preloaded) return; // Preload completed
      if (label.dataset.preloadTimeout) return; // Preload waiting
      if (label.matches('.opt-btn:checked + .opt-label, [aria-selected="true"]')) return; // Already selected

      // Build option array if clicked
      const input = label.htmlFor ? document.getElementById(label.htmlFor) : label;
      const optionIds = this.getSelectedOptionIds();
      const inputOptionIndex = Array.from(this.optionSelectors).indexOf(input.closest('.option-selector'));
      optionIds[inputOptionIndex] = input.dataset.valueId;

      // If all options are selected
      if (optionIds.indexOf(null) === -1) {
        // Wait to predict click-intent and preload link
        label.dataset.preloadTimeout = setTimeout(() => {
          label.dataset.preloaded = true;
          label.removeAttribute('data-preload-timeout');
          const combinedProductUrl = input.dataset.productUrl;
          const url = this.constructVariantUrl(
            combinedProductUrl,
            optionIds,
            input.dataset.variantId
          );
          theme.fetchCache.preload(url.toString());
        }, 250);
      }
    }

    /**
     * Handles 'mouseleave' events on option labels. Cancels link preload if in/out fast.
     * @param {object} evt - Event object.
     */
    static handleLabelMouseLeave(evt) {
      const label = evt.currentTarget;
      clearTimeout(label.dataset.preloadTimeout);
      label.removeAttribute('data-preload-timeout');
    }

    /**
     * Create a URL for fetching a product or variant.
     * @param {string} combinedProductUrl - URL for a combined product. Optional.
     * @param {Array} optionIds - Selected option IDs to include in the URL. Optional.
     * @param {string} variantId - Variant ID to use if not a combined product. Optional.
     * @returns {URL} Variant URL
     */
    constructVariantUrl(combinedProductUrl, optionIds, variantId) {
      let url = null;
      if (combinedProductUrl) {
        url = new URL(combinedProductUrl, window.location.origin);
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        url = new URL(this.dataset.url, window.location.origin);
      }
      const sectionIds = [this.dataset.sectionId];
      if (!this.quickBuyContainer && !this.featuredProductContainer) {
        document.querySelectorAll('.cc-variant-dependent-section [data-section-id]').forEach((e) => {
          sectionIds.push(e.dataset.sectionId);
        });
      }
      url.searchParams.set('sections', sectionIds.join(','));
      // V5 replaces the entire section after selection. During a pending update,
      // an option's contextual variant ID can still describe the previous colour.
      // Read the complete current selection so rapid changes resolve correctly.
      if (combinedProductUrl || !variantId || optionIds.includes(null) || this.section.querySelector('.pdp-v5')) {
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        url.searchParams.set('variant', variantId);
      }
      return url;
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      const selectedOptionIds = this.getSelectedOptionIds();

      // Immediately update swatch label
      this.updateSwatchLabel();

      // Construct new variant URL and fetch it, using theme cache
      const combinedProductUrl = evt.target.dataset.productUrl || evt.detail?.productUrl;
      const variantId = evt.target.dataset.variantId || evt.detail?.variantId;
      const url = this.constructVariantUrl(combinedProductUrl, selectedOptionIds, variantId);
      this.variantRequestId += 1;
      const currentRequestId = this.variantRequestId;

      theme.fetchCache.fetch(url.toString())
        .then((responseText) => {
          if (currentRequestId !== this.variantRequestId) return;

          const responseData = JSON.parse(responseText);

          // Update content
          Object.entries(responseData).forEach(([section, htmlStr]) => {
            const html = new DOMParser().parseFromString(htmlStr, 'text/html');
            const sectionId = `shopify-section-${section}`;
            let target = this.section;
            let newContent = html.getElementById(sectionId);

            if (this.section.id !== sectionId) {
              if (!this.quickBuyContainer) {
                target = document.getElementById(sectionId);
              } else {
                const quickBuyTemplate = html.getElementById('quick-buy-template')?.content.querySelector('.js-product');
                if (quickBuyTemplate) newContent = quickBuyTemplate;
              }
            }

            this.updateContent(target, newContent, combinedProductUrl);
          });

          // Restore focus
          if (!evt.detail?.noFocus) {
            document.querySelector(`#${evt.target.id}-button, input#${evt.target.id}`)?.focus();
          }

          // Update URL and announce changes
          const newVariantPicker = this.section.querySelector('.pdp-v5')
            ? document.getElementById(this.section.id)?.querySelector('variant-picker')
            : this.section.querySelector('variant-picker');
          if (newVariantPicker) setTimeout(() => newVariantPicker.announceChange(), 10);
        });
    }

    /**
     * Announces the current variant after a variant change.
     */
    announceChange() {
      const selectedOptionIds = this.getSelectedOptionIds();
      const selectedOptions = this.getSelectedOptions();
      const productForm = this.getProductForm();
      // The V5 product section is replaced with Shopify's selected-variant HTML.
      // Its embedded variant JSON is authoritative; this theme does not expose
      // a complete client-side variant list for getVariantFromOptions().
      const currentVariant = this.section.querySelector('.pdp-v5')
        ? this.getVariantData()
        : this.getVariantFromOptions(selectedOptions);

      // Update current variant data
      this.variant = currentVariant;

      // Update hidden variant input(s)
      productForm.querySelectorAll('input[name="id"]').forEach((input) => {
        input.value = currentVariant ? currentVariant.id : '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      if (this.section.querySelector('.pdp-v5') && this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        if (currentVariant) url.searchParams.set('variant', currentVariant.id);
        else url.searchParams.delete('variant');
        window.history.replaceState({}, '', url);
      }

      // Dispatch event to allow other components to react
      this.dispatchEvent(new CustomEvent('variant-change', {
        bubbles: true,
        detail: {
          variant: currentVariant,
          optionIds: selectedOptionIds,
          options: selectedOptions,
        },
      }));

      // Update variant content
      this.updateVariantContent();
    }

    updateContent(target, newContent, combinedProductUrl) {
      if (!target || !newContent) return;

      // Quick buy content transformation
      if (this.quickBuyContainer) {
        const qb = newContent.querySelector('.js-product');
        if (qb) theme.quickBuy.convertToQuickBuyContent(qb);
      }

      // Replace target section HTML
      target.replaceWith(newContent);

      // Update URL (optional)
      const shouldUpdateUrl = this.dataset.updateUrl === 'true';
      if (shouldUpdateUrl && !this.quickBuyContainer && !this.featuredProductContainer) {
        const url = combinedProductUrl ? new URL(combinedProductUrl, window.location.origin) : new URL(window.location.href);
        window.history.replaceState({}, '', url);
      }
    }

    updateStatusClasses() {
      const variantPicker = this.section.querySelector('variant-picker');
      if (!variantPicker) return;

      if (this.variant) {
        variantPicker.classList.toggle('variant-picker--variant-selected', true);
        variantPicker.classList.toggle('variant-picker--variant-unavailable', !this.variant.available);
      } else {
        variantPicker.classList.toggle('variant-picker--variant-selected', false);
        variantPicker.classList.toggle('variant-picker--variant-unavailable', true);
      }
    }

    updateAddToCartButton() {
      const productForm = this.getProductForm();
      if (!productForm) return;
      const button = productForm.querySelector('[name="add"]');
      if (!button) return;

      if (!this.variant) {
        button.disabled = true;
        button.setAttribute('aria-disabled', true);
      } else {
        button.disabled = !this.variant.available;
        button.setAttribute('aria-disabled', !this.variant.available);
      }
    }

    updateSwatchLabel() {
      this.optionSelectors.forEach((selector) => {
        if (!selector.classList.contains('option-selector') || selector.dataset.option === '') return;
        const labelValue = selector.querySelector('.option-selector__label-value');
        if (!labelValue) return;
        const selected = selector.querySelector('input:checked') || selector.querySelector('.custom-select__option[aria-selected="true"]');
        if (!selected) return;
        labelValue.textContent = selected.value || selected.dataset.value || '';
      });
    }

    updateAvailability() {
      // Existing theme availability logic (kept as-is)
    }

    getVariantData() {
      const variantJson = this.querySelector('script[type="application/json"]');
      if (!variantJson) return null;
      try {
        return JSON.parse(variantJson.textContent);
      } catch (e) {
        return null;
      }
    }

    getVariantFromOptions(selectedOptions) {
      const variants = this.variant?.product?.variants || window.__shopify_product_variants__ || null;
      if (!variants || !Array.isArray(variants)) return null;
      return variants.find((v) => v.options?.every((opt, idx) => opt === selectedOptions[idx])) || null;
    }

    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get the selected option value ids from a list of variant options.
     * @returns {Array} Array of selected option value ids, value is null if not selected.
     */
    getSelectedOptionIds() {
      const selectedOptionIds = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptionIds.push(selected && selected.dataset.value !== '' ? selected.dataset.valueId : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptionIds.push(selected ? selected.dataset.valueId : null);
        }
      });

      return selectedOptionIds;
    }

    /**
     * Apply search parameters (set by product card swatches).
     */
    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find((x) => x.dataset.option === key);
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
          const colorOptionDropdown = matchingOptionSelector.querySelector(`.custom-select__option[data-value="${CSS.escape(value)}"]:not([aria-selected="true"])`);
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest('custom-select');
            const convertEvent = (evt) => {
              evt.detail.noFocus = true;
              customSelect.dispatchEvent(
                new CustomEvent('change', { bubbles: true, cancelable: false, detail: evt.detail })
              );
            };
            customSelect.addEventListener('change', convertEvent, { once: true });
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = matchingOptionSelector.querySelector(`input[value="${CSS.escape(value)}"]:not(:checked)`);
          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector('.option-selector__label-value');
            if (matchingOptionValue) {
              matchingOptionSelector.querySelector('.option-selector__label-value').textContent = value;
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent('change', { bubbles: true, cancelable: false, detail: { noFocus: true } })
            );
          }
        }
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
}

/* ============================================================
   Bodycare Size Guide modal (theme-agnostic)
   - Opens/closes .bc-sg-modal created by variant-picker.liquid
   - Works after section re-renders (event delegation)
============================================================ */
if (!window.bcSizeGuideModal) {
  window.bcSizeGuideModal = (() => {
    let lastFocus = null;

    const getModal = (id) => document.getElementById(id);

    const open = (id, triggerEl) => {
      const modal = getModal(id);
      if (!modal) return;

      lastFocus = triggerEl || document.activeElement;
      modal.hidden = false;

      // Prevent background scroll (simple + safe)
      document.documentElement.style.overflow = 'hidden';

      // Focus close button (accessibility)
      const closeBtn = modal.querySelector('[data-bc-sg-close]');
      if (closeBtn && typeof closeBtn.focus === 'function') closeBtn.focus();
    };

    const close = (id) => {
      const modal = getModal(id);
      if (!modal) return;

      modal.hidden = true;

      // Restore scroll only if no other size-guide modal is open
      if (!document.querySelector('.bc-sg-modal:not([hidden])')) {
        document.documentElement.style.overflow = '';
      }

      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus();
      }
      lastFocus = null;
    };

    // Open / close (delegated)
    document.addEventListener('click', (e) => {
      const openBtn = e.target.closest('[data-bc-sg-open]');
      if (openBtn) {
        e.preventDefault();
        open(openBtn.getAttribute('data-bc-sg-open'), openBtn);
        return;
      }

      const closeBtn = e.target.closest('[data-bc-sg-close]');
      if (closeBtn) {
        e.preventDefault();
        close(closeBtn.getAttribute('data-bc-sg-close'));
      }
    });

    // Escape key closes the currently open modal
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const openModalEl = document.querySelector('.bc-sg-modal:not([hidden])');
      if (!openModalEl) return;
      e.preventDefault();
      close(openModalEl.id);
    });

    return { open, close };
  })();
}
